import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import '../swal/sweetalert.css';
import { getToken } from '../../../utils/themeTokens';

const MySwal = withReactContent(Swal);

/* LabelFlow theme colors — read lazily from the design tokens so both themes stay correct */
const THEME = {
  get primary() { return getToken('--primary', '#059669'); },
  get primaryHover() { return getToken('--primary-hover', '#047857'); },
  get destructive() { return getToken('--destructive', '#dc2626'); },
  get cancel() { return getToken('--muted-foreground', '#64748b'); },
};

const getIconByType = (iconType) => {
    let returnURL;

    switch (iconType) {
        case 'good':
            returnURL =
                "data:image/svg+xml,%0A%3Csvg xmlns='http://www.w3.org/2000/svg' height='48' width='48'%3E%3Cpath fill='%23059669' d='M24 44q-4.15 0-7.8-1.575-3.65-1.575-6.35-4.275-2.7-2.7-4.275-6.35Q4 28.15 4 24t1.575-7.8Q7.15 12.55 9.85 9.85q2.7-2.7 6.35-4.275Q19.85 4 24 4q3.15 0 5.975.9 2.825.9 5.225 2.5l-3.05 3.05Q30.35 9.4 28.3 8.8q-2.05-.6-4.3-.6-6.55 0-11.175 4.625Q8.2 17.45 8.2 24q0 6.55 4.625 11.175Q17.45 39.8 24 39.8q6.55 0 11.175-4.625Q39.8 30.55 39.8 24q0-.85-.075-1.625T39.5 20.85l3.4-3.4q.55 1.55.825 3.175Q44 22.25 44 24q0 4.15-1.575 7.8-1.575 3.65-4.275 6.35-2.7 2.7-6.35 4.275Q28.15 44 24 44Zm-2.75-10.85-8.55-8.6 2.95-2.95 5.65 5.65L41.05 7.5 44 10.4Z'/%3E%3C/svg%3E";
            break;
        case 'bad':
            returnURL =
                "data:image/svg+xml,%0A%3Csvg xmlns='http://www.w3.org/2000/svg' height='48' width='48'%3E%3Cpath fill='%23dc2626' d='M24 34q.7 0 1.175-.475.475-.475.475-1.175 0-.7-.475-1.175Q24.7 30.7 24 30.7q-.7 0-1.175.475-.475.475-.475 1.175 0 .7.475 1.175Q23.3 34 24 34Zm-1.35-7.65h3V13.7h-3ZM24 44q-4.1 0-7.75-1.575-3.65-1.575-6.375-4.3-2.725-2.725-4.3-6.375Q4 28.1 4 23.95q0-4.1 1.575-7.75 1.575-3.65 4.3-6.35 2.725-2.7 6.375-4.275Q19.9 4 24.05 4q4.1 0 7.75 1.575 3.65 1.575 6.35 4.275 2.7 2.7 4.275 6.35Q44 19.85 44 24q0 4.1-1.575 7.75-1.575 3.65-4.275 6.375t-6.35 4.3Q28.15 44 24 44Z'/%3E%3C/svg%3E";
            break;
        default:
            returnURL = '';
    }
    return returnURL;
};

export async function swalConfirmation(swalSize, iconType, htmlTitle, htmlString, confirmationButtonText, cancelButtonText, responseFn, confirmButtonColor) {
    let icon = getIconByType(iconType);
    swalSize = swalSize ? swalSize : '400';
    MySwal.fire({
        width: swalSize,
        imageUrl: icon,
        title: htmlTitle,
        html: htmlString,
        showCancelButton: true,
        reverseButtons: false,
        confirmButtonColor: confirmButtonColor || THEME.primary,
        cancelButtonColor: THEME.cancel,
        confirmButtonText: confirmationButtonText ? confirmationButtonText : 'OK',
        cancelButtonText: cancelButtonText ? cancelButtonText : 'Cancel',
    }).then((result) => {
        return responseFn(result);
    });
}

let alertInProgress = false;

export async function swalTimeoutLoaderBase(
  swalSize = '400',
  iconType,
  htmlTitle,
  htmlString,
  confirmationButtonText = 'OK',
  cancelButtonText = 'Cancel',
  responseFn = () => {},
  confirmButtonColor = THEME.primary,
  autoConfirmTimeout = 10
) {
  if (alertInProgress) {
    console.warn("Alert already in progress. Skipping duplicate.");
    return;
  }
// Close any existing dialog
  MySwal.close();
  alertInProgress = true;

  const icon = getIconByType(iconType);
  let timerInterval = null;
  let countdown = autoConfirmTimeout;

  try {
    await MySwal.fire({
      width: swalSize,
      imageUrl: icon,
      title: htmlTitle,
      html: htmlString,
      showCancelButton: true,
      reverseButtons: false,
      confirmButtonColor: confirmButtonColor || THEME.primary,
      cancelButtonColor: THEME.cancel,
      confirmButtonText: confirmationButtonText,
      cancelButtonText: cancelButtonText,
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        if (autoConfirmTimeout) {
          timerInterval = setInterval(() => {
            countdown -= 1;
            if (countdown <= 0) {
              clearInterval(timerInterval);
              timerInterval = null;
              if (MySwal.isVisible()) {
                Swal.clickConfirm();
              }
            }
          }, 1000);
        }
      },
      willClose: () => {
        if (timerInterval) {
          clearInterval(timerInterval);
          timerInterval = null;
        }
      }
    }).then((result) => {
      if (timerInterval) {
        clearInterval(timerInterval);
        timerInterval = null;
      }
      responseFn(result);
    });
  } finally {
    alertInProgress = false;
  }
}




export async function swalThreeButtonConfirmation(swalSize, iconType, htmlTitle, htmlString, confirmationButtonText, cancelButtonText, denyButtonText, responseFn) {
    let icon = getIconByType(iconType);
    swalSize = swalSize ? swalSize : '400';
    MySwal.fire({
        width: swalSize,
        imageUrl: icon,
        title: htmlTitle,
        html: htmlString, // Use html instead of text
        icon: icon,
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonColor: THEME.primary,
        cancelButtonColor: THEME.cancel,
        denyButtonColor: THEME.destructive,
        confirmButtonText: confirmationButtonText ? confirmationButtonText : 'Yes',
        denyButtonText: denyButtonText ? denyButtonText : 'No', // Custom text for the deny button
        cancelButtonText: cancelButtonText ? cancelButtonText : 'Later' // Custom text for the cancel button
    }).then((result) => {
        return responseFn(result);
    });
}

export async function swal(swalSize, iconType, htmlTitle, htmlString, confirmationButtonText, responseFn, confirmButtonColor) {
    let icon = getIconByType(iconType);
    swalSize = swalSize ? swalSize : '400';
    MySwal.fire({
        width: swalSize,
        imageUrl: icon,
        title: htmlTitle,
        html: htmlString,
        confirmButtonText: confirmationButtonText ? confirmationButtonText : 'OK',
        confirmButtonColor: confirmButtonColor,
    }).then((result) => {
        return responseFn(result);
    });
}
export async function swalCloseOut(swalSize, iconType, htmlTitle, htmlString, confirmationButtonText, responseFn, confirmButtonColor) {
    swalSize = swalSize ? swalSize : '400';
    MySwal.close();
}

export async function swalLoaderPop(swalSize, iconType, htmlTitle, htmlString, confirmationButtonText, responseFn, confirmButtonColor) {
    let icon = getIconByType(iconType);
// Close any existing dialog
  MySwal.close();
    swalSize = swalSize ? swalSize : '400';
    MySwal.fire({
        width: swalSize,
        imageUrl: icon,
        title: htmlTitle,
        html: htmlString,
        showConfirmButton: false, // No confirm button
        allowOutsideClick: false,  // Disable closing by clicking outside
        didOpen: () => {
            Swal.showLoading(); // Show loading animation
        }
    });
}

/**
 * @function
 * @description alert
 * @returns
 * @example
 * import {useSwal} from "...Swal"
 * const { warning } = useSwal()
 * warning('htmlMessage', fn())
 */
export const useSwal = () => {
    // const translation = useTranslation();

    const swalSuccess = (htmlTitle = '', htmlMessage = '', callback = () => { }) => {
        swalOneBtnPositive(htmlTitle, htmlMessage, 'good', (callback = callback));
    };

    const swalFail = (htmlTitle = '', htmlMessage = '', callback = () => { }) => {
        swalOneBtnPositive(htmlTitle, htmlMessage, 'bad', (callback = callback));
    };

    const swalWarning = (htmlTitle = '', htmlMessage = '', callback = () => { }) => {
        swalTwoBtnNegative(htmlTitle, htmlMessage, '', (callback = callback));
    };

    const swalConfirm = (htmlTitle = '', htmlMessage = '', callback = () => { }) => {
        swalTwoBtnPositive(htmlTitle, htmlMessage, 'good', callback);
    };
    const swalUpdateConfirm = (htmlTitle = '', htmlMessage = '', callback = () => { }) => {
        swalThreeBtnPositive(htmlTitle, htmlMessage, 'good', callback);
    };
    const swalLoader = (htmlTitle = '', htmlMessage = '', callback = () => { }) => {
        swalLoaderFn(htmlTitle, htmlMessage, 'good', (callback = callback));
    };
    const swalLogout = (htmlTitle = '', htmlMessage = '', callback = () => { }) => {
        swalTwoBtnPositive(htmlTitle, htmlMessage, 'bad', (callback = callback));
    };
    const swalTimeoutLoader = (htmlTitle = '', htmlMessage = '', callback = () => { }, swalTimeoutLoader = 10000) => {
        swalTimeoutLoaderFn(htmlTitle, htmlMessage, 'good', (callback = callback), swalTimeoutLoader);
    };
    const swalClose = (htmlTitle = '', htmlMessage = '', callback = () => { }) => {
        swalCloseFn(htmlTitle, htmlMessage, '', (callback = callback));
    };
    //파란색 글씨 일 땐 span 태그, 검은색일 땐 p 태그로 감싸야함
    const swalTimeoutLoaderFn = (htmlTitle, htmlMessage, icon = 'good', callback = () => { }, autoConfirmTimeout) => {
        var iconType = icon;
        var swalWidth = '400';
        var title = htmlTitle;

        var htmlString = htmlMessage;
        var confirmButtonColor = THEME.primary;
        var cancelButtonText = "Cancel" //translation.cancel;
        var confirmationButtonText = "OK" //translation.confirm;
        var responseFn = callback;
        swalTimeoutLoaderBase(swalWidth, iconType, title, htmlString, confirmationButtonText, cancelButtonText, responseFn, confirmButtonColor, autoConfirmTimeout);
    };

    const swalTwoBtnPositive = (htmlTitle, htmlMessage, icon = 'good', callback = () => { }) => {
        var iconType = icon;
        var swalWidth = '400';
        var title = htmlTitle;

        var htmlString = htmlMessage;
        var confirmButtonColor = THEME.primary;
        var cancelButtonText = "Cancel" //translation.cancel;
        var confirmationButtonText = "OK" //translation.confirm;
        var responseFn = callback;
        swalConfirmation(swalWidth, iconType, title, htmlString, confirmationButtonText, cancelButtonText, responseFn, confirmButtonColor);
    };

    const swalThreeBtnPositive = (htmlTitle, htmlMessage, icon = 'good', callback = () => { }) => {
        var iconType = icon;
        var swalWidth = '800';
        var title = htmlTitle;
        var htmlString = htmlMessage;
        var denyButtonText = 'No';
        var cancelButtonText = "Cancel" //translation.cancel;
        var confirmationButtonText = "OK" //translation.confirm;
        var responseFn = callback;
        swalThreeButtonConfirmation(swalWidth, iconType, title, htmlString, confirmationButtonText, cancelButtonText, denyButtonText, responseFn);
    };
    const swalTwoBtnNegative = (htmlTitle, htmlMessage, icon = 'bad', callback = () => { }) => {
        var title = htmlTitle;
        var iconType = icon;
        var swalWidth = '400';
        var htmlString = htmlMessage;
        var confirmButtonColor = THEME.primary;
        var cancelButtonText = "Cancel" //translation.cancel;
        var confirmationButtonText = "OK" //translation.confirm;
        var responseFn = callback;

        swalConfirmation(swalWidth, iconType, title, htmlString, confirmationButtonText, cancelButtonText, responseFn, confirmButtonColor);
    };
    const swalOneBtnPositive = (htmlTitle, htmlMessage, icon = 'good', callback = () => { }) => {
        var title = htmlTitle;
        var iconType = icon;
        var swalWidth = '400';
        var htmlString = htmlMessage;
        var confirmButtonColor = THEME.primary;
        var confirmationButtonText = "OK" //translation.confirm;
        var responseFn = callback;
        swal(swalWidth, iconType, title, htmlString, confirmationButtonText, responseFn, confirmButtonColor);
    };
    const swalOneBtnNegative = (htmlTitle, htmlMessage, icon = 'bad', callback = () => { }) => {
        var title = htmlTitle;
        var iconType = icon;
        var swalWidth = '400';
        var htmlString = htmlMessage;
        var confirmButtonColor = THEME.primary;
        var confirmationButtonText = "OK" //translation.confirm;
        var responseFn = callback;
        swal(swalWidth, iconType, title, htmlString, confirmationButtonText, responseFn, confirmButtonColor);
    };

    const swalLoaderFn = (htmlTitle, htmlMessage, icon = 'good', callback = () => { }) => {
        var title = htmlTitle;
        var iconType = '';
        var swalWidth = '';
        var htmlString = htmlMessage;
        var confirmButtonColor = THEME.primary;
        var confirmationButtonText = "OK" //translation.confirm;
        var responseFn = callback;
        swalLoaderPop(swalWidth, iconType, title, htmlString, confirmationButtonText, responseFn, confirmButtonColor);
    };
    const swalCloseFn = (htmlTitle, htmlMessage, icon = 'good', callback = () => { }) => {
        var title = htmlTitle;
        var iconType = icon;
        var swalWidth = '400';
        var htmlString = htmlMessage;
        var confirmButtonColor = THEME.primary;
        var confirmationButtonText = "OK" //translation.confirm;
        var responseFn = callback;
        swalCloseOut(swalWidth, iconType, title, htmlString, confirmationButtonText, responseFn, confirmButtonColor);
    };
    return { swalSuccess, swalFail, swalWarning, swalConfirm, swalLogout, swalLoader, swalClose, swalUpdateConfirm, swalTimeoutLoader };
};
